// Shape of the MARA agent dialog's state and its translation to and from the API payload. Kept
// beside the dialog so the form stays a render of named pieces rather than one long component.

/** Salutations form 956 prints as fixed boxes, plus its free-text escape hatch. */
export const TITLE_OPTIONS = [
  { value: "MR", label: "Mr" },
  { value: "MRS", label: "Mrs" },
  { value: "MISS", label: "Miss" },
  { value: "MS", label: "Ms" },
  { value: "OTHER", label: "Other" },
];

/** What the agent is registered as. Decides which registration number a government form asks for. */
export const AGENT_TYPE_OPTIONS = [
  { value: "REGISTERED_MIGRATION_AGENT", label: "Registered migration agent (MARN)" },
  { value: "LEGAL_PRACTITIONER", label: "Legal practitioner (LPN)" },
  { value: "EXEMPT_PERSON", label: "Exempt person" },
];

const emptyAddress = { street: "", suburb: "", country: "", postcode: "" };
const emptyPhone = { countryCode: "", areaCode: "", number: "" };

export const emptyMaraForm = {
  consultantAgent: "",
  agentName: "",
  title: "",
  titleOther: "",
  agentType: "",
  maraRegistrationNo: "",
  lpn: "",
  businessAddress: "",
  address: { ...emptyAddress },
  usesCorrespondenceAddress: false,
  correspondenceAddress: { ...emptyAddress },
  officePhone: { ...emptyPhone },
  state: "",
  email: "",
  mobile: "",
  phone: "",
  scopes: [],
};

const toAddressForm = (address) => ({
  street: address?.street || "",
  suburb: address?.suburb || "",
  country: address?.country || "",
  postcode: address?.postcode || "",
});

const hasAnyValue = (parts) => Object.values(parts).some((value) => String(value || "").trim());

export const toMaraForm = (agent) => {
  if (!agent) return emptyMaraForm;
  const correspondenceAddress = toAddressForm(agent.correspondenceAddress);
  return {
    consultantAgent: agent.consultantAgent || "",
    agentName: agent.agentName || "",
    title: agent.title || "",
    titleOther: agent.titleOther || "",
    agentType: agent.agentType || "",
    maraRegistrationNo: agent.maraRegistrationNo || "",
    lpn: agent.lpn || "",
    businessAddress: agent.businessAddress || "",
    address: toAddressForm(agent.address),
    // Nothing stored means post goes to the business address, which is what a government form
    // means by "AS ABOVE"; the checkbox reflects that rather than showing four empty boxes.
    usesCorrespondenceAddress: hasAnyValue(correspondenceAddress),
    correspondenceAddress,
    officePhone: {
      countryCode: agent.officePhone?.countryCode || "",
      areaCode: agent.officePhone?.areaCode || "",
      number: agent.officePhone?.number || "",
    },
    state: agent.state || "",
    email: agent.email || "",
    mobile: agent.mobile || "",
    phone: agent.phone || "",
    scopes: agent.scopes || [],
  };
};

const trimmedOrNull = (value) => (String(value || "").trim() ? String(value).trim() : null);

const toAddressPayload = (address) => ({
  street: trimmedOrNull(address.street),
  suburb: trimmedOrNull(address.suburb),
  country: trimmedOrNull(address.country),
  postcode: trimmedOrNull(address.postcode),
});

export const toMaraRequest = (form) => ({
  consultantAgent: trimmedOrNull(form.consultantAgent),
  agentName: form.agentName.trim(),
  title: form.title || null,
  titleOther: form.title === "OTHER" ? trimmedOrNull(form.titleOther) : null,
  agentType: form.agentType || null,
  maraRegistrationNo: trimmedOrNull(form.maraRegistrationNo),
  lpn: trimmedOrNull(form.lpn),
  businessAddress: trimmedOrNull(form.businessAddress),
  address: toAddressPayload(form.address),
  correspondenceAddress: form.usesCorrespondenceAddress
    ? toAddressPayload(form.correspondenceAddress)
    : null,
  officePhone: {
    countryCode: trimmedOrNull(form.officePhone.countryCode),
    areaCode: trimmedOrNull(form.officePhone.areaCode),
    number: trimmedOrNull(form.officePhone.number),
  },
  state: trimmedOrNull(form.state),
  email: trimmedOrNull(form.email),
  mobile: trimmedOrNull(form.mobile),
  phone: trimmedOrNull(form.phone),
  scopes: form.scopes,
});
